import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './main.scss';
import App from './App';
import { FusionAuthProvider } from '@fusionauth/react-sdk';
import { ThemeService } from './services/themeService';
import mapboxgl from 'mapbox-gl';
import { configService, getFusionAuthConfig, getMapboxToken } from './services/configService';
import { installRefreshCoordinator } from './utils/refreshCoordinator';

// Mapbox GL caps concurrent tile/image fetches at 16 by default, which throttled
// map fill once the per-frame render cost was fixed. The backend/edge/GeoServer
// comfortably handle far more, so lift the cap for faster parallel tile loading.
mapboxgl.maxParallelImageRequests = 48;

// Install cross-tab refresh coordinator BEFORE any module makes a fetch.
// Wraps window.fetch so /app/refresh/ calls are serialized across tabs via
// the Web Locks API, eliminating the OneTimeUse refresh-token race.
installRefreshCoordinator();

// Capture deep-link URL BEFORE React mounts and any <Navigate to="/login">
// can wipe the query string. App.tsx restores from sessionStorage on auth.
// Only /map is stashed — other paths (notably /app/front-channel-logout) must
// never be replayed post-login, or the FA logout iframe target turns into a
// blank top-level page on every login.
(() => {
  try {
    const POST_LOGIN_URL_KEY = 'em3:postLoginUrl';
    const path = window.location.pathname;
    const search = window.location.search;
    if (path === '/map' && search.length > 0 && !sessionStorage.getItem(POST_LOGIN_URL_KEY)) {
      sessionStorage.setItem(POST_LOGIN_URL_KEY, window.location.href);
    }
  } catch { /* sessionStorage may be unavailable in some environments */ }
})();

// No global 401 interceptor — forcing /logout on any 401 revokes the
// refresh token, which forces the next visit through a full code flow
// and creates a new FA session row. The SDK's auto-refresh and the
// visibility-driven refreshToken() in App.tsx handle expiry.

// Initialize theme system and runtime config before rendering React app
async function initializeApp() {
  try {
    // CRITICAL: Load runtime config BEFORE anything else
    // In production, this fetches /config.json generated by entrypoint.sh
    // In development, this falls back to import.meta.env
    await configService.load();
    console.log('[main] Runtime config loaded successfully');
    // Set the Mapbox access token before any component mounts. Setting this
    // in App.tsx's useEffect was a race — child components (EasyMap) mount
    // and instantiate `new mapboxgl.Map(...)` before parent useEffects run.
    mapboxgl.accessToken = getMapboxToken();
  } catch (error) {
    console.error('[main] Failed to load runtime config:', error);
    throw error; // Can't continue without config
  }

  // Pre-fetch user identity so the synchronous map init in EasyMap has a
  // real userId for viewport restore. The FA SDK's userInfo fetch is async
  // and races map init, causing viewport to load under a null key.
  try {
    const { serverUrl } = getFusionAuthConfig();
    const meUrl = new URL('/app/me/', serverUrl).toString();
    const meRes = await fetch(meUrl, { credentials: 'include' });
    if (meRes.ok) {
      const me = await meRes.json();
      if (me?.sub) {
        sessionStorage.setItem('em3:bootUserId', me.sub);
      }
    } else if (meRes.status === 401) {
      sessionStorage.removeItem('em3:bootUserId');
    }
  } catch (error) {
    console.warn('[main] Pre-fetch user identity failed:', error);
  }

  try {
    // Load theme system (core.css + user's selected theme)
    await ThemeService.initialize();
  } catch (error) {
    console.error('Failed to initialize theme system, continuing with defaults:', error);
  }

  // Replace the bundled FWI2025 defaults with whatever the backend currently
  // serves from fire_danger_classification. Falls back to defaults if the
  // call fails — see fwiClassification.ts.
  try {
    const { initFWIClassifications } = await import('./utils/fwiClassification');
    await initFWIClassifications();
  } catch (error) {
    console.warn('[main] FWI classification init failed; using bundled defaults:', error);
  }

  // Get FusionAuth config from runtime config service
  const fusionAuthConfig = {
    ...getFusionAuthConfig(),
    shouldAutoFetchUserInfo: true,
    shouldAutoRefresh: true,
    responseType: 'code', // Use Authorization Code grant with PKCE
    scope: 'openid profile offline_access', // offline_access required for refresh tokens
    onAutoRefreshFailure: (error: unknown) => {
      // A single tab's refresh failure does NOT necessarily mean the user is
      // logged out. With OneTimeUse refresh tokens, only one tab wins a
      // concurrent refresh race; the losers receive a 400. The losing tab
      // must NOT call /app/logout — that would clear the shared parent-domain
      // cookies (.intellifirenwt.com) and log every other tab out too.
      //
      // Wait briefly for any winning tab's refresh response to update the
      // shared cookies, then re-check app.at_exp (the only non-HttpOnly app
      // cookie). If a fresh expiry is set, another tab refreshed for us —
      // reload this tab to let the SDK pick up the new state. If app.at_exp
      // is genuinely past/missing, then the session really is gone and we
      // can call /app/logout to clean up.
      console.warn('[FusionAuth] Auto-refresh failed; checking shared cookie state before forcing logout', error);
      window.setTimeout(() => {
        const atExpRaw = document.cookie
          .split('; ')
          .find((c) => c.startsWith('app.at_exp='))?.split('=')[1];
        const atExpMs = atExpRaw ? parseInt(atExpRaw, 10) * 1000 : 0;
        const stillAuthed = atExpMs > Date.now();
        if (stillAuthed) {
          console.log('[FusionAuth] Shared cookies refreshed by another tab; reloading to recover.');
          window.location.reload();
          return;
        }
        console.error('[FusionAuth] No valid session on shared cookies; logging out.');
        window.location.href = '/app/logout';
      }, 1500);
    },
  };

  // Render React application
  createRoot(document.getElementById('root') as HTMLElement).render(
    <FusionAuthProvider {...fusionAuthConfig}>
      <StrictMode>
        <App />
      </StrictMode>
    </FusionAuthProvider>
  );
}

// Start application
initializeApp();
